import { useSubscription, ResourcePackType, RESOURCE_PACKS } from './useSubscription';
import { trackEvent } from '@/lib/posthog';

export type AIFeatureType =
  | 'strategy'
  | 'documents'
  | 'sage'
  | 'trade_proposals'
  | 'legal_documents'
  | 'support_calculations'
  | 'strategic_insights';

interface AILimitCheck {
  canUse: boolean;
  used: number;
  limit: number | null;
  remaining: number | null;
  percentUsed: number;
  isNearLimit: boolean; // Within 20% of limit
  suggestedPack: ResourcePackType | null;
}

// Map feature types to the resource pack that refills them
const FEATURE_TO_PACK: Record<AIFeatureType, ResourcePackType> = {
  strategy: 'strategy_boost',
  documents: 'document_pack',
  sage: 'full_refill', // No specific pack for sage, suggest full refill
  trade_proposals: 'strategy_boost', // Trade proposals use strategy credits
  legal_documents: 'export_pack',
  support_calculations: 'full_refill', // No specific pack, suggest full refill
  strategic_insights: 'strategy_boost', // Strategic insights use strategy credits
};

export function useAILimits() {
  const {
    subscription,
    tier,
    isPaid,
    canRegenerate,
    canUploadDocument,
    canSendSageMessage,
    canGenerateTradeProposal,
    canGenerateLegalDocument,
    canCalculateSupport,
    canGenerateStrategicInsight,
    getUsageStats,
    purchaseResourcePack,
    isPurchasingResourcePack,
  } = useSubscription();

  const usageStats = getUsageStats();

  // Check a specific AI feature
  const checkFeature = (feature: AIFeatureType): AILimitCheck => {
    let canUse = false;
    let used = 0;
    let limit: number | null = null;

    switch (feature) {
      case 'strategy':
        canUse = canRegenerate();
        used = usageStats.regenerations.used;
        limit = usageStats.regenerations.limit;
        break;
      case 'documents':
        canUse = canUploadDocument();
        used = usageStats.documents.used;
        limit = usageStats.documents.limit;
        break;
      case 'sage':
        canUse = canSendSageMessage();
        used = usageStats.sageMessages.used;
        limit = usageStats.sageMessages.limit;
        break;
      case 'trade_proposals':
        canUse = canGenerateTradeProposal();
        used = usageStats.tradeProposals.used;
        limit = usageStats.tradeProposals.limit;
        break;
      case 'legal_documents':
        canUse = canGenerateLegalDocument();
        used = usageStats.legalDocuments.used;
        limit = usageStats.legalDocuments.limit;
        break;
      case 'support_calculations':
        canUse = canCalculateSupport();
        used = usageStats.supportCalculations.used;
        limit = usageStats.supportCalculations.limit;
        break;
      case 'strategic_insights':
        canUse = canGenerateStrategicInsight();
        used = usageStats.strategicInsights.used;
        limit = usageStats.strategicInsights.limit;
        break;
    }

    // -1 or null = unlimited
    const isUnlimited = limit === null || limit === -1;
    const remaining = isUnlimited ? null : Math.max(0, limit - used);
    const percentUsed = isUnlimited ? 0 : (limit > 0 ? (used / limit) * 100 : 0);
    const isNearLimit = percentUsed >= 80;

    // Only suggest pack if user is paid (can purchase packs) and at/near limit
    const suggestedPack = isPaid && !canUse ? FEATURE_TO_PACK[feature] : null;

    return {
      canUse,
      used,
      limit,
      remaining,
      percentUsed,
      isNearLimit,
      suggestedPack,
    };
  };

  // Get all limits at once
  const getAllLimits = (): Record<AIFeatureType, AILimitCheck> => ({
    strategy: checkFeature('strategy'),
    documents: checkFeature('documents'),
    sage: checkFeature('sage'),
    trade_proposals: checkFeature('trade_proposals'),
    legal_documents: checkFeature('legal_documents'),
    support_calculations: checkFeature('support_calculations'),
    strategic_insights: checkFeature('strategic_insights'),
  });

  // Check if any feature is near its limit
  const hasAnyNearLimit = (): boolean => {
    const limits = getAllLimits();
    return Object.values(limits).some((l) => l.isNearLimit);
  };

  // Get features that are at their limit
  const getExhaustedFeatures = (): AIFeatureType[] => {
    const limits = getAllLimits();
    return (Object.entries(limits) as [AIFeatureType, AILimitCheck][])
      .filter(([, check]) => !check.canUse && check.limit !== null && check.limit > 0)
      .map(([feature]) => feature);
  };

  // Get user-friendly name for a feature
  const getFeatureName = (feature: AIFeatureType): string => {
    const names: Record<AIFeatureType, string> = {
      strategy: 'Strategy Regenerations',
      documents: 'Document Uploads',
      sage: 'Sage AI Messages',
      trade_proposals: 'Trade Proposals',
      legal_documents: 'Legal Document Exports',
      support_calculations: 'Support Calculations',
      strategic_insights: 'Strategic Insights',
    };
    return names[feature];
  };

  // Get the pack info for a feature
  const getPackForFeature = (feature: AIFeatureType) => {
    const packType = FEATURE_TO_PACK[feature];
    return {
      type: packType,
      ...RESOURCE_PACKS[packType],
    };
  };

  // Track when a user attempts an action but is blocked by limits
  const trackBlockedAttempt = (feature: AIFeatureType) => {
    const check = checkFeature(feature);
    if (!check.canUse) {
      trackEvent('feature_attempt_blocked', {
        feature,
        tier,
        used: check.used,
        limit: check.limit,
      });
    }
  };

  return {
    // Check specific feature
    checkFeature,

    // Get all limits
    getAllLimits,

    // Helpers
    hasAnyNearLimit,
    getExhaustedFeatures,
    getFeatureName,
    getPackForFeature,
    trackBlockedAttempt,

    // Current tier info
    tier,
    isPaid,

    // Purchase resource pack
    purchaseResourcePack,
    isPurchasingResourcePack,

    // Raw usage stats
    usageStats,
  };
}

// Helper component for displaying usage
export function formatUsage(used: number, limit: number | null): string {
  // -1 or null = unlimited
  if (limit === null || limit === -1) return `${used} used (unlimited)`;
  return `${used} / ${limit}`;
}

export function getUsageColor(percentUsed: number): string {
  if (percentUsed >= 100) return 'text-red-500';
  if (percentUsed >= 80) return 'text-amber-500';
  if (percentUsed >= 50) return 'text-yellow-500';
  return 'text-green-500';
}

export function getProgressColor(percentUsed: number): string {
  if (percentUsed >= 100) return 'bg-red-500';
  if (percentUsed >= 80) return 'bg-amber-500';
  if (percentUsed >= 50) return 'bg-yellow-500';
  return 'bg-green-500';
}
